/**
 * Smart-merge dedupe: lets users re-scan a handwritten list that has grown
 * since yesterday (or dump an updated text list) without duplicating the
 * items that were already imported.
 *
 * Matching is fuzzy on purpose — OCR and dictation render the same
 * handwritten item slightly differently between scans ("Call the dentist"
 * vs "call dentist!"), so we compare normalized token sets rather than
 * raw strings. Pure functions, no store or DOM dependencies.
 */

import type { ArchivedTask, ClassifiedTask, Task } from '../types';

/** Lowercase, strip punctuation/bullets, collapse whitespace. */
export function normalizeItemText(text: string): string {
  return text
    .toLowerCase()
    .replace(/[‘’']/g, '') // don't → dont (keeps token identity)
    .replace(/[^a-z0-9\s]/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();
}

/** Filler words that shouldn't decide whether two items match. */
const STOPWORDS = new Set([
  'the', 'a', 'an', 'to', 'my', 'our', 'for', 'of', 'and', 'on', 'in', 'at',
  'about', 're', 'with', 'up',
]);

function significantTokens(text: string): Set<string> {
  const all = normalizeItemText(text).split(' ').filter(Boolean);
  const kept = all.filter((t) => !STOPWORDS.has(t));
  // An item made entirely of stopwords still needs something to compare.
  return new Set(kept.length > 0 ? kept : all);
}

/**
 * Are these two list items the same task?
 * - Exact match after normalization always matches.
 * - Otherwise compare significant-token overlap: short items (≤2 tokens)
 *   must agree fully; longer items tolerate ~20% OCR/wording noise, which
 *   also treats "email John" vs "email John re the contract" as the same
 *   item rewritten with more detail on a later scan.
 */
export function sameItem(a: string, b: string): boolean {
  const na = normalizeItemText(a);
  const nb = normalizeItemText(b);
  if (!na || !nb) return false;
  if (na === nb) return true;
  // Spacing-insensitive match: OCR renders "PermaBox" / "Perma box" /
  // "Perma Box" interchangeably between scans of the same handwriting.
  if (na.replace(/ /g, '') === nb.replace(/ /g, '')) return true;
  const ta = significantTokens(a);
  const tb = significantTokens(b);
  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter += 1;
  const minSize = Math.min(ta.size, tb.size);
  if (minSize === 0) return false;
  const overlap = inter / minSize;
  return minSize <= 2 ? overlap === 1 : overlap >= 0.8;
}

/**
 * Similarity score between two items: 1 for a confident match, else the
 * significant-token overlap relative to the smaller item. Exposed so the
 * merge can define an "uncertain" band between duplicate and new.
 */
export function itemSimilarity(a: string, b: string): number {
  const na = normalizeItemText(a);
  const nb = normalizeItemText(b);
  if (!na || !nb) return 0;
  if (na === nb || na.replace(/ /g, '') === nb.replace(/ /g, '')) return 1;
  const ta = significantTokens(a);
  const tb = significantTokens(b);
  let inter = 0;
  for (const t of ta) if (tb.has(t)) inter += 1;
  const minSize = Math.min(ta.size, tb.size);
  if (minSize === 0) return 0;
  // A single shared word ("call dentist" / "call Mark") is coincidence,
  // not resemblance — never treat it as a possible match.
  if (inter < 2) return 0;
  return inter / minSize;
}

/**
 * The honesty band: not confident enough to silently skip, too similar to
 * silently import. Week to week, users reword the same task ("Resolve
 * Anchor & Align Website Issue" → "Anchor & Align: Website Fix") — when we
 * can't tell, we ASK the user rather than fabricate a duplicate or silently
 * swallow a genuinely new task.
 */
/**
 * Floor of the ask-band. 0.6, not 0.5: two DIFFERENT tasks under the same
 * project header share the header's tokens ("Anchor & Align: Bank Account"
 * vs "Anchor & Align: Issue Shares" → 2 shared of 4 = 0.5), and asking
 * about every sibling task would train users to ignore the question.
 */
export const UNCERTAIN_MIN = 0.6;

export function possiblySame(a: string, b: string): boolean {
  if (sameItem(a, b)) return false; // that's a confident match, not a maybe
  return itemSimilarity(a, b) >= UNCERTAIN_MIN;
}

/** An incoming item needing the user's judgment. Two kinds:
 *  - 'similar' (default): resembles an existing open task — same or new?
 *  - 'completed': the scan saw a tick/strikethrough — record as done,
 *    add as a to-do anyway, or ignore? Asked, never guessed: a misread
 *    strikethrough silently burying real work would be the worst failure. */
export interface UncertainMatch {
  item: ClassifiedTask;
  /** The existing open task text it resembles ('' for kind 'completed'). */
  matchText: string;
  kind?: 'similar' | 'completed';
}

export interface MergeResult {
  /** Genuinely new items to import. */
  fresh: ClassifiedTask[];
  /** Skipped: item is already open in the current week. */
  alreadyOpen: number;
  /** Skipped: item was already completed this week. */
  alreadyDone: number;
  /** Needs the user's judgment — possible rewordings of existing tasks. */
  uncertain: UncertainMatch[];
}

/**
 * Split freshly classified items into new-vs-known against the current
 * week. Items matching a completed task (still on the board or archived)
 * are counted separately — the paper list doesn't know you finished them.
 * Also drops duplicates *within* the incoming batch (e.g. two photos that
 * both caught the same line).
 */
export function mergeNewItems(
  classified: ClassifiedTask[],
  currentTasks: Task[],
  archive: ArchivedTask[],
): MergeResult {
  const openTexts = currentTasks.filter((t) => !t.done).map((t) => t.text);
  const doneTexts = [
    ...currentTasks.filter((t) => t.done).map((t) => t.text),
    ...archive.map((a) => a.text),
  ];

  const fresh: ClassifiedTask[] = [];
  const uncertain: UncertainMatch[] = [];
  let alreadyOpen = 0;
  let alreadyDone = 0;

  for (const item of classified) {
    if (doneTexts.some((t) => sameItem(t, item.text))) {
      alreadyDone += 1;
      continue;
    }
    if (openTexts.some((t) => sameItem(t, item.text))) {
      alreadyOpen += 1;
      continue;
    }
    if (fresh.some((f) => sameItem(f.text, item.text))) {
      // Intra-batch duplicate (same line captured twice) — silently drop.
      continue;
    }
    // The honesty band: resembles an open task but we can't be sure it IS
    // that task reworded. Better to ask than to output false data — pick
    // the closest open task as the candidate and let the user decide.
    let best: string | undefined;
    let bestScore = 0;
    for (const t of openTexts) {
      const score = itemSimilarity(t, item.text);
      if (score > bestScore) {
        bestScore = score;
        best = t;
      }
    }
    if (best !== undefined && bestScore >= UNCERTAIN_MIN) {
      uncertain.push({ item, matchText: best });
      continue;
    }
    fresh.push(item);
  }

  return { fresh, alreadyOpen, alreadyDone, uncertain };
}

/** Human summary of a smart merge, or undefined when nothing was skipped. */
export function mergeNotice(result: MergeResult): string | undefined {
  const { fresh, alreadyOpen, alreadyDone } = result;
  const skipped = alreadyOpen + alreadyDone;
  if (skipped === 0) return undefined;
  // (uncertain items get their own interactive review card, not a notice)
  const parts: string[] = [];
  if (alreadyOpen > 0) parts.push(`${alreadyOpen} already in your week`);
  if (alreadyDone > 0) parts.push(`${alreadyDone} already completed`);
  if (fresh.length === 0) {
    return `Nothing new to add — ${parts.join(' and ')}. Your matrix is up to date.`;
  }
  return `Smart merge: ${fresh.length} new task${fresh.length === 1 ? '' : 's'} added · ${parts.join(
    ' · ',
  )} — no duplicates created.`;
}
